import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  digits,
  EMAIL_RE,
  Footer,
  Frame,
  Header,
  Intro,
  KvkkTrio,
  type ConsentState,
  type PersonPayload,
  type Product,
  type VehiclePayload,
  type DwellingPayload,
  type TravelPayload,
  type HealthPayload,
  RequestReceived,
  RegisterOtp,
  ScrollBody,
  StatusBar,
  StepHeading,
  SubmitError,
  TcknField,
  TextField,
  PhoneField,
  BirthDatePicker,
  SearchableSelect,
  TURKIYE_ILLERI,
  useIntakeSubmit,
  useRuhsatUpload,
  type RuhsatBlock,
  type RuhsatUploadStatus,
} from "./shared";

// WEB3 — the generic ürün teklif-TALEBİ engine. Request-first model: the web funnel
// CAPTURES A REQUEST and submits it — it NEVER shows an instant price/quote. The
// advisor prepares the 3–4 offers AFTERWARDS and delivers on the customer's choice.
// All five product pages are the SAME 2-step flow — ① ürün bilgileri → ② kişisel +
// KVKK (the intake POST) → terminal A4 "Talebiniz alındı" success screen — differing
// only in the ① fields + whether the line carries a VEHICLE asset + saglik's extra
// health açık rıza. Each page supplies a ProductConfig; this engine owns state,
// validation, the intake POST (the ② boundary), and the shared terminal. Responsive
// by construction: it renders the shared <Frame>, the 03 phone bezel on mobile and a
// flat wide card on desktop (styles.css .csa-basvuru-*).
//
// Deviation from 03 (deliberate, noted in the WEB3 report): 03's in-phone "sigorta
// türü" selector step is dropped — each product is reached from its own homepage card,
// so the flow opens straight on that product's fields. Uniform across all five lines.

export interface InfoCtx {
  fields: Record<string, string>;
  setField: (k: string) => (v: string) => void;
  errors: Record<string, string>;
  ruhsatFile: File | null;
  setRuhsatFile: (f: File | null) => void;
  ruhsatEscape: boolean;
  setRuhsatEscape: (v: boolean) => void;
  // Live ruhsat upload status (KARAR-4) — surfaced by RuhsatField.
  ruhsatStatus: RuhsatUploadStatus;
  ruhsatProgress: number;
  ruhsatUploadError: string | null;
}

export interface ProductConfig {
  product: Product;
  intro: { eyebrow: string; title: string; text: string };
  headerTitle: string;
  infoTitle: string;
  infoLead: string;
  /** Extra "sağlık açık rıza" consent (saglik only). */
  extraHealthConsent?: boolean;
  renderInfo: (ctx: InfoCtx) => ReactNode;
  validateInfo: (ctx: InfoCtx) => Record<string, string>;
  /** VEHICLE lines only (trafik/kasko) — builds the vehicle block for the POST. */
  buildVehicle?: (fields: Record<string, string>) => VehiclePayload | undefined;
  /** DASK only — builds the dwelling block (webIntakeSchema.dwelling). */
  buildDwelling?: (fields: Record<string, string>) => DwellingPayload | undefined;
  /** Seyahat only — builds the travel block (webIntakeSchema.travel). */
  buildTravel?: (fields: Record<string, string>) => TravelPayload | undefined;
  /** Sağlık only — builds the health block (webIntakeSchema.health). */
  buildHealth?: (fields: Record<string, string>) => HealthPayload | undefined;
  summary: (fields: Record<string, string>) => { label: string; value: string };
}

const TOTAL = 2;

// Terminal sub-mode once the request is submitted (step 3). "received" is the calm A4
// success screen; "register" is the OPTIONAL self-serve account path (System 2).
type Fork = "received" | "register";

export function BasvuruFlow({ config }: { config: ProductConfig }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [fork, setFork] = useState<Fork>("received");
  // Client-side talep referans no, minted once the neutral 202 receipt lands (the
  // POST returns no id by design — anti-enumeration). Display-only.

  const [fields, setFields] = useState<Record<string, string>>({});
  const [ruhsatFile, setRuhsatFile] = useState<File | null>(null);
  const [ruhsatEscape, setRuhsatEscape] = useState(false);

  const [consents, setConsents] = useState<ConsentState>({
    aydinlatma: false,
    hizmetKosullari: false,
    mesafeliOnBilgilendirme: false,
  });
  const [healthConsent, setHealthConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { submit, submitting, error: submitErr, setError: setSubmitErr } = useIntakeSubmit();
  const ruhsat = useRuhsatUpload();

  const setField = (k: string) => (v: string) => setFields((f) => ({ ...f, [k]: v }));
  const get = (k: string) => fields[k] ?? "";

  // Attaching a file kicks off the real encrypted upload; clearing/escaping resets it.
  function handleRuhsatFile(f: File | null) {
    setRuhsatFile(f);
    if (f) ruhsat.upload(f);
    else ruhsat.reset();
  }
  function handleRuhsatEscape(v: boolean) {
    setRuhsatEscape(v);
    if (v) {
      setRuhsatFile(null);
      ruhsat.reset();
    }
  }

  const infoCtx: InfoCtx = {
    fields,
    setField,
    errors,
    ruhsatFile,
    setRuhsatFile: handleRuhsatFile,
    ruhsatEscape,
    setRuhsatEscape: handleRuhsatEscape,
    ruhsatStatus: ruhsat.status,
    ruhsatProgress: ruhsat.progress,
    ruhsatUploadError: ruhsat.error,
  };

  function validatePersonal(): boolean {
    const e: Record<string, string> = {};
    if (!get("firstName").trim()) e.firstName = "Ad zorunlu.";
    if (!get("lastName").trim()) e.lastName = "Soyad zorunlu.";
    if (digits(get("tckn")).length !== 11) e.tckn = "TC Kimlik No 11 haneli olmalı.";
    if (!get("birthDate").trim()) e.birthDate = "Doğum tarihi zorunlu.";
    if (digits(get("phone")).length !== 10) e.phone = "Geçerli bir cep telefonu girin.";
    if (!EMAIL_RE.test(get("email").trim())) e.email = "Geçerli bir e-posta girin.";
    if (!get("il").trim()) e.il = "İl zorunlu.";
    if (!get("ilce").trim()) e.ilce = "İlçe zorunlu.";
    // Meslek is OPTIONAL (meslekSchema.optional()) so it never gates submit; when
    // present it must satisfy the schema's max-120 bound (inline, like other fields).
    if (get("meslek").trim().length > 120) e.meslek = "Meslek en fazla 120 karakter olmalı.";
    if (!consents.aydinlatma) e.aydinlatma = "Devam etmek için aydınlatma metnini onaylayın.";
    if (!consents.hizmetKosullari)
      e.hizmetKosullari = "Devam etmek için hizmet koşullarını onaylayın.";
    if (!consents.mesafeliOnBilgilendirme)
      e.mesafeliOnBilgilendirme = "Devam etmek için ön bilgilendirme formunu onaylayın.";
    if (config.extraHealthConsent && !healthConsent)
      e.healthConsent = "Sağlık verileriniz için ayrı açık rızanız gerekli.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function buildPerson(): PersonPayload {
    // WEB3-B: birthDate/il/ilçe are now accepted by webIntakeSchema.person (optional),
    // so they ride along. They are validated non-empty in validatePersonal above.
    // Other door fields (ruhsat / SGK / UAVT …) are still not in the DTO.
    const meslek = get("meslek").trim();
    return {
      firstName: get("firstName").trim(),
      lastName: get("lastName").trim(),
      birthDate: get("birthDate").trim(),
      il: get("il").trim(),
      ilce: get("ilce").trim(),
      // Optional occupation — omit the key entirely when blank so the .strict()
      // door sees `undefined` (meslekSchema is min(1) when present).
      ...(meslek ? { meslek } : {}),
      email: get("email").trim(),
      phone: `+90${digits(get("phone"))}`,
      tckn: digits(get("tckn")),
    };
  }

  function nextFromInfo() {
    const e = config.validateInfo(infoCtx);
    // KARAR-4: when a ruhsat file is attached (and not escaped), block advancing
    // until the encrypted upload has resolved to a token — the intake body carries
    // the token, never the binary.
    if (ruhsatFile && !ruhsatEscape && ruhsat.status !== "done") {
      e.ruhsat =
        ruhsat.status === "uploading"
          ? "Ruhsat yükleniyor, lütfen bekleyin."
          : (ruhsat.error ?? "Ruhsat yüklenemedi. Lütfen tekrar deneyin.");
    }
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStep(2);
  }

  // KARAR-4: the ruhsat metadata/escape block for the intake body (VEHICLE lines
  // only — the schema rejects a ruhsat block on a non-vehicle line).
  function buildRuhsat(): RuhsatBlock | undefined {
    if (!config.buildVehicle) return undefined;
    if (ruhsatEscape) return { deferredToPhone: true };
    if (ruhsat.meta) return ruhsat.meta;
    return undefined;
  }

  async function nextFromPersonal() {
    if (!validatePersonal()) return;
    // ② boundary: the real PUBLIC intake POST records the lead/quote-request. Only
    // advance to the terminal A4 success screen on the neutral 202 receipt — no
    // price/quote is ever shown; the advisor prepares the offers afterwards.
    const result = await submit({
      product: config.product,
      vehicle: config.buildVehicle ? config.buildVehicle(fields) : undefined,
      ruhsat: buildRuhsat(),
      // Each block is supplied only by its own product's config, so exactly the
      // selected line's block rides — the schema's line-binding (superRefine) holds.
      dwelling: config.buildDwelling ? config.buildDwelling(fields) : undefined,
      travel: config.buildTravel ? config.buildTravel(fields) : undefined,
      health: config.buildHealth ? config.buildHealth(fields) : undefined,
      // Health açık rıza is required on (and only on) the saglik line — passed only
      // when the line carries the extra consent and the user granted it.
      saglikAcikRiza: config.extraHealthConsent && healthConsent ? true : undefined,
      person: buildPerson(),
    });
    if (result.ok) {
      setFork("received");
      setStep(3);
      return;
    }
    // Field-attributed server validation → render each error INLINE at its field
    // (in red), merged over the client-side errors just set. Anything the server
    // couldn't attribute stays in the generic <SubmitError> banner (submitErr).
    const fe = result.fieldErrors;
    if (fe) setErrors((prev) => ({ ...prev, ...fe }));
  }

  function back() {
    setErrors({});
    setSubmitErr(null);
    if (step === 3) {
      // From the optional register path, back returns to the A4 success screen; the
      // A4 itself is terminal (the request is already recorded) so it stays put.
      if (fork === "register") setFork("received");
      return;
    }
    if (step === 1) navigate("/");
    else setStep((s) => s - 1);
  }

  const summary = config.summary(fields);
  const phone = `+90${digits(get("phone"))}`;

  return (
    <main className="csa-basvuru-page">
      <div className="csa-basvuru-shell">
        <Intro eyebrow={config.intro.eyebrow} title={config.intro.title} text={config.intro.text} />

        <Frame>
          {step < 3 ? (
            <Header title={headerTitle(config, step)} step={step} total={TOTAL} onBack={back} />
          ) : (
            <StatusBar />
          )}

          {step === 3 ? (
            <div style={{ flex: 1, overflowY: "auto", padding: "30px 22px" }}>
              {fork === "received" && (
                <RequestReceived
                  summaryLabel={summary.label}
                  summaryValue={summary.value}
                  onRegister={() => setFork("register")}
                  onHome={() => navigate("/")}
                />
              )}
              {fork === "register" && (
                <RegisterOtp
                  phone={phone}
                  tckn={digits(get("tckn"))}
                  onHome={() => navigate("/")}
                />
              )}
            </div>
          ) : (
            <ScrollBody>
              {step === 1 && (
                <>
                  <StepHeading title={config.infoTitle} lead={config.infoLead} />
                  {config.renderInfo(infoCtx)}
                </>
              )}
              {step === 2 && (
                <>
                  <StepHeading title="Sizi tanıyalım" />
                  <TextField
                    id="ad"
                    label="Ad"
                    value={get("firstName")}
                    onChange={setField("firstName")}
                    error={errors.firstName}
                  />
                  <TextField
                    id="soyad"
                    label="Soyad"
                    value={get("lastName")}
                    onChange={setField("lastName")}
                    error={errors.lastName}
                  />
                  <TcknField value={get("tckn")} onChange={setField("tckn")} error={errors.tckn} />
                  <BirthDatePicker
                    value={get("birthDate")}
                    onChange={setField("birthDate")}
                    error={errors.birthDate}
                  />
                  <PhoneField
                    value={get("phone")}
                    onChange={setField("phone")}
                    error={errors.phone}
                  />
                  <TextField
                    id="email"
                    label="E-posta"
                    type="email"
                    value={get("email")}
                    onChange={setField("email")}
                    placeholder="ornek@eposta.com"
                    error={errors.email}
                  />
                  <SearchableSelect
                    id="il"
                    label="İl"
                    value={get("il")}
                    onChange={setField("il")}
                    options={TURKIYE_ILLERI}
                    placeholder="İl arayın veya seçin"
                    error={errors.il}
                  />
                  <TextField
                    id="ilce"
                    label="İlçe"
                    value={get("ilce")}
                    onChange={setField("ilce")}
                    placeholder="Örn. Kadıköy"
                    error={errors.ilce}
                  />
                  <TextField
                    id="meslek"
                    label="Meslek"
                    value={get("meslek")}
                    onChange={setField("meslek")}
                    placeholder="Örn. Öğretmen (isteğe bağlı)"
                    error={errors.meslek}
                  />
                  <KvkkTrio
                    state={consents}
                    onChange={(patch) => setConsents((c) => ({ ...c, ...patch }))}
                    errors={errors}
                    extra={
                      config.extraHealthConsent ? (
                        <SaglikConsent
                          checked={healthConsent}
                          onChange={setHealthConsent}
                          error={errors.healthConsent}
                        />
                      ) : undefined
                    }
                  />
                  <SubmitError msg={submitErr} />
                </>
              )}
            </ScrollBody>
          )}

          {step === 1 && (
            <Footer
              label="Devam et"
              onClick={nextFromInfo}
              loading={ruhsat.status === "uploading"}
            />
          )}
          {step === 2 && (
            <Footer
              label="Talebi gönder"
              onClick={() => void nextFromPersonal()}
              loading={submitting}
            />
          )}
        </Frame>
      </div>
    </main>
  );
}

function headerTitle(config: ProductConfig, step: number): string {
  if (step === 1) return config.headerTitle;
  return "Kişisel bilgiler";
}

// Saglik-only extra consent, rendered inside the KVKK block (AYRI sağlık açık rıza,
// KARAR-2). The health DECLARATION itself is OFFLINE — the advisor collects it later;
// only this consent is captured at the door.
function SaglikConsent({
  checked,
  onChange,
  error,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  error?: string;
}) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 11, padding: "8px 0 4px" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(ev) => onChange(ev.target.checked)}
        style={{ width: 18, height: 18, flex: "0 0 auto", marginTop: 1, accentColor: "#00249E" }}
        aria-label="Sağlık verileri açık rıza onayı"
      />
      <span style={{ fontSize: 12, lineHeight: 1.5, color: "#7A8896" }}>
        Sağlık verilerimin, teklif hazırlanması amacıyla işlenmesine{" "}
        <b style={{ color: "#16294C" }}>ayrı açık rıza</b> veriyorum. (Sağlık beyanı danışmanınız
        tarafından alınır.)
        {error && <span style={{ display: "block", color: "#D24A43", marginTop: 4 }}>{error}</span>}
      </span>
    </label>
  );
}
